// Public Enrollment Form - Multi-step with Stripe Payment
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, DollarSign, AlertCircle, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PortalLayout from './PortalLayout';
import StudentSelectionModal from '../common/StudentSelectionModal';
import StripePaymentForm from '../payment/StripePaymentForm';
import { formatPhoneInput, formatTime12Hour, formatDate } from '../../utils/formatters';

const PublicEnrollment = ({ classId: propClassId }) => {
  const { classId: paramClassId } = useParams();
  const classId = propClassId || paramClassId;
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(true);
  const [currentStep, setCurrentStep] = useState(1); // 1: Info, 2: Review, 3: Payment
  const [classData, setClassData] = useState(null);
  const [classSessions, setClassSessions] = useState([]);
  const [portalSettings, setPortalSettings] = useState(null);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [sameAsParent, setSameAsParent] = useState(false);
  const [followupClasses, setFollowupClasses] = useState([]);
  const [selectedFollowupClasses, setSelectedFollowupClasses] = useState([]);

  const [formData, setFormData] = useState({
    studentFirstName: '',
    studentLastName: '',
    studentEmail: '',
    studentPhone: '',
    studentGender: '',
    dateOfBirth: '',
    parentFirstName: '',
    parentLastName: '',
    parentEmail: '',
    parentPhone: '',
    relationship: 'Parent',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    medicalConditions: '',
    specialNeeds: '',
    medications: '',
    paymentOption: 'full',
    paymentMethod: 'credit_card',
    paymentStatus: 'pending',
    enrollmentDate: new Date().toISOString().split('T')[0],
    termsAgreement: false,
    photoReleaseAgreement: false,
    emergencyTreatmentAgreement: false
  });

  // Load Stripe
  useEffect(() => {
    const loadStripeKey = async () => {
      if (!classId) return;
      try {
        const response = await fetch(`/api/payments/config?classId=${classId}`);
        const data = await response.json();
        if (data.error) {
          console.error('Stripe config error:', data.message);
          return;
        }
        setStripePromise(loadStripe(data.publishableKey));
      } catch (error) {
        console.error('Error loading Stripe:', error);
      }
    };
    loadStripeKey();
  }, [classId]);

  // Fetch class data, sessions, and portal settings
  useEffect(() => {
    const fetchClassData = async () => {
      try {
        // Fetch class details
        const classResponse = await fetch(`/api/classes/public/${classId}`);
        if (!classResponse.ok) throw new Error('Class not found');
        const classDataJson = await classResponse.json();
        setClassData(classDataJson.class);

        const userId = classDataJson.class.userId;

        if (userId) {
          // Fetch portal settings and teacher info
          const [teacherResponse, sessionsResponse] = await Promise.all([
            fetch(`/api/portal/${userId}/teacher`),
            fetch(`/api/classes/${classId}/sessions`)
          ]);

          if (teacherResponse.ok) {
            const teacherData = await teacherResponse.json();
            // The endpoint returns teacher info with portal settings combined
            setPortalSettings(teacherData.teacher);
            setTeacherInfo(teacherData.teacher);
          }

          if (sessionsResponse.ok) {
            const sessionsData = await sessionsResponse.json();
            setClassSessions(sessionsData.sessions || []);
          }
        }

        // Fetch follow-up classes if they exist
        if (classDataJson.class.followupClassIds && classDataJson.class.followupClassIds.length > 0) {
          try {
            const followupPromises = classDataJson.class.followupClassIds.map(id =>
              fetch(`/api/classes/public/${id}`).then(res => res.json())
            );
            const followupResults = await Promise.all(followupPromises);
            const validFollowupClasses = followupResults
              .filter(result => result.class)
              .map(result => result.class);
            setFollowupClasses(validFollowupClasses);
          } catch (followupErr) {
            console.error('Error loading follow-up classes:', followupErr);
            // Non-critical error, don't show to user
          }
        }
      } catch (err) {
        setError('Unable to load class information');
      } finally {
        setLoading(false);
      }
    };
    if (classId) {
      fetchClassData();
    }
  }, [classId]);

  // Handle same as parent checkbox
  useEffect(() => {
    if (sameAsParent) {
      setFormData(prev => ({
        ...prev,
        emergencyContactName: `${prev.parentFirstName} ${prev.parentLastName}`.trim(),
        emergencyContactPhone: prev.parentPhone,
        emergencyContactRelationship: prev.relationship
      }));
    }
  }, [sameAsParent, formData.parentFirstName, formData.parentLastName, formData.parentPhone, formData.relationship]);

  const handleStudentSelected = (student) => {
    if (student) {
      setFormData(prev => ({
        ...prev,
        studentFirstName: student.studentFirstName || '',
        studentLastName: student.studentLastName || '',
        studentGender: student.gender || '',
        dateOfBirth: student.dateOfBirth || '',
        parentFirstName: student.parentName ? student.parentName.split(' ')[0] : '',
        parentLastName: student.parentName ? student.parentName.split(' ').slice(1).join(' ') : '',
        parentEmail: student.parentEmail || '',
        parentPhone: student.parentPhone || '',
        emergencyContactName: student.emergencyContactName || '',
        emergencyContactPhone: student.emergencyContactPhone || '',
        emergencyContactRelationship: student.emergencyContactRelationship || '',
        medicalConditions: student.medicalNotes || '',
      }));
    }
    setShowModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;

    // Format phone numbers
    if (name === 'parentPhone' || name === 'emergencyContactPhone') {
      finalValue = formatPhoneInput(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleSameAsParentChange = (e) => {
    const checked = e.target.checked;
    setSameAsParent(checked);

    if (!checked) {
      // Clear emergency contact fields when unchecked
      setFormData(prev => ({
        ...prev,
        emergencyContactName: '',
        emergencyContactPhone: '',
        emergencyContactRelationship: ''
      }));
    }
  };

  const validateStep = () => {
    if (currentStep === 1) {
      const required = ['studentFirstName', 'studentLastName', 'dateOfBirth', 'studentGender'];
      const isMinor = formData.dateOfBirth && (new Date().getFullYear() - new Date(formData.dateOfBirth).getFullYear()) < 18;
      if (isMinor) {
        required.push('parentFirstName', 'parentLastName', 'parentEmail', 'parentPhone');
      }
      const missing = required.filter(field => !formData[field]);
      if (missing.length > 0) {
        setError(`Please fill in all required fields`);
        return false;
      }
    }
    if (currentStep === 2) {
      if (!formData.termsAgreement) {
        setError('Please agree to the terms and conditions');
        return false;
      }
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  // Create enrollment and payment intent
  const handleProceedToPayment = async () => {
    if (!validateStep()) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/enrollments/public', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          classId,
          ...formData,
          paymentStatus: 'pending',
          followupClassIds: selectedFollowupClasses // Include selected follow-up classes
        })
      });

      const data = await response.json();
      if (!response.ok) {
        // Provide user-friendly error messages
        let errorMessage = data.message || 'Enrollment failed';
        if (errorMessage.includes('already enrolled')) {
          errorMessage = 'This student is already enrolled in this class. Please select a different class or student.';
        }
        throw new Error(errorMessage);
      }

      setEnrollmentId(data.enrollment.id);
      setClientSecret(data.clientSecret);
      setCurrentStep(3);
    } catch (err) {
      setError(err.message);
      window.scrollTo(0, 0); // Scroll to top to show error
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Update enrollment with payment success
      const response = await fetch(`/api/enrollments/${enrollmentId}/payment`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          paymentStatus: 'paid'
        })
      });

      if (!response.ok) throw new Error('Failed to update enrollment');

      setSubmitted(true);
    } catch (err) {
      setError('Payment processed but enrollment update failed. Please contact support.');
    }
  };

  const handlePaymentError = (error) => {
    setError(error.message || 'Payment failed. Please try again.');
  };

  if (loading) {
    return (
      <PortalLayout portalSettings={portalSettings} teacherInfo={teacherInfo}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading class information...</p>
          </div>
        </div>
      </PortalLayout>
    );
  }

  if (submitted) {
    return (
      <PortalLayout portalSettings={portalSettings} teacherInfo={teacherInfo}>
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-900 mb-2">Enrollment Successful!</h2>
            <p className="text-green-700 mb-4">
              Thank you for enrolling in <strong>{classData?.title}</strong>
            </p>
            <p className="text-sm text-green-600 mb-6">
              A confirmation email has been sent to <strong>{formData.parentEmail || formData.studentEmail}</strong>
            </p>
            <button
              onClick={() => navigate(classData?.userId ? `/portal/${classData.userId}` : '/')}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Return to Classes
            </button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  const isMinor = formData.dateOfBirth && (new Date().getFullYear() - new Date(formData.dateOfBirth).getFullYear()) < 18;

  return (
    <PortalLayout portalSettings={portalSettings} teacherInfo={teacherInfo}>
      <StudentSelectionModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onStudentSelected={handleStudentSelected}
      />

      <div className="max-w-4xl mx-auto p-6">
        {/* Progress Steps */}
        <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {['Information', 'Review', 'Payment'].map((step, idx) => (
              <React.Fragment key={idx}>
                {/* Connector Line */}
                {idx !== 0 && (
                  <div className="flex-1 h-0.5 mx-4 relative">
                    <div className="absolute inset-0 bg-gray-200" />
                    <div
                      className={`absolute inset-0 transition-all duration-300 ${
                        currentStep > idx ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                      style={{ width: currentStep > idx ? '100%' : '0%' }}
                    />
                  </div>
                )}

                {/* Step Circle and Label */}
                <div className="flex flex-col items-center gap-2 min-w-[80px]">
                  <div className={`
                    flex items-center justify-center w-12 h-12 rounded-full font-semibold text-sm
                    transition-all duration-300 shadow-sm
                    ${currentStep > idx + 1 ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
                      currentStep === idx + 1 ? 'bg-blue-600 text-white ring-4 ring-blue-100 scale-110' :
                      'bg-gray-100 text-gray-500 border-2 border-gray-300'}
                  `}>
                    {currentStep > idx + 1 ? <Check className="w-6 h-6" /> : idx + 1}
                  </div>
                  <span className={`
                    text-xs font-medium text-center transition-colors duration-300
                    ${currentStep === idx + 1 ? 'text-blue-600 font-semibold' :
                      currentStep > idx + 1 ? 'text-gray-700' :
                      'text-gray-500'}
                  `}>
                    {step}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Class Info Header */}
        {classData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{classData.title}</h1>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4" />
                Classes Begin on {classData.startDate && formatDate(classData.startDate)}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4" />
                {classData.startTime && formatTime12Hour(classData.startTime)}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4" />
                {classData.location}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <DollarSign className="w-4 h-4" />
                ${classData.price}
              </div>
            </div>

            {/* Class Sessions */}
            {classSessions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-2">Class Sessions:</h3>
                <div className="grid md:grid-cols-2 gap-2 text-sm text-gray-600">
                  {classSessions.slice(0, 6).map((session, idx) => (
                    <div key={idx}>
                      {formatDate(session.session_date)} at {formatTime12Hour(session.start_time)}
                    </div>
                  ))}
                  {classSessions.length > 6 && (
                    <div className="text-gray-500 italic">
                      ...and {classSessions.length - 6} more sessions
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Step 1: Information */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* Student Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Student Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="studentFirstName"
                    value={formData.studentFirstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="studentLastName"
                    value={formData.studentLastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender *
                  </label>
                  <select
                    name="studentGender"
                    value={formData.studentGender}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Parent/Guardian Information (if minor) */}
            {isMinor && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Parent/Guardian Information</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="parentFirstName"
                      value={formData.parentFirstName}
                      onChange={handleInputChange}
                      required={isMinor}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="parentLastName"
                      value={formData.parentLastName}
                      onChange={handleInputChange}
                      required={isMinor}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="parentEmail"
                      value={formData.parentEmail}
                      onChange={handleInputChange}
                      required={isMinor}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="parentPhone"
                      value={formData.parentPhone}
                      onChange={handleInputChange}
                      required={isMinor}
                      placeholder="(XXX) XXX-XXXX"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship *
                    </label>
                    <select
                      name="relationship"
                      value={formData.relationship}
                      onChange={handleInputChange}
                      required={isMinor}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Parent">Parent</option>
                      <option value="Grandparent">Grandparent</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Friend">Friend</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Emergency Contact */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Emergency Contact</h2>

              {isMinor && (
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sameAsParent}
                      onChange={handleSameAsParentChange}
                      className="rounded"
                    />
                    <span className="text-sm text-gray-700">
                      Same as Parent/Guardian
                    </span>
                  </label>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    name="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={handleInputChange}
                    disabled={sameAsParent}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleInputChange}
                    disabled={sameAsParent}
                    placeholder="(XXX) XXX-XXXX"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship
                  </label>
                  <select
                    name="emergencyContactRelationship"
                    value={formData.emergencyContactRelationship}
                    onChange={handleInputChange}
                    disabled={sameAsParent}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Select...</option>
                    <option value="Parent">Parent</option>
                    <option value="Grandparent">Grandparent</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Medical Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Medical Information (Optional)</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Medical Conditions
                  </label>
                  <textarea
                    name="medicalConditions"
                    value={formData.medicalConditions}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Special Needs/Accommodations
                  </label>
                  <textarea
                    name="specialNeeds"
                    value={formData.specialNeeds}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleNext}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
              >
                Continue to Review
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review & Agreements */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Review Your Information</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Student</h3>
                  <p className="text-gray-700">{formData.studentFirstName} {formData.studentLastName}</p>
                  <p className="text-sm text-gray-600">DOB: {formData.dateOfBirth}</p>
                </div>

                {isMinor && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Parent/Guardian</h3>
                    <p className="text-gray-700">{formData.parentFirstName} {formData.parentLastName}</p>
                    <p className="text-sm text-gray-600">{formData.parentEmail}</p>
                    <p className="text-sm text-gray-600">{formData.parentPhone}</p>
                  </div>
                )}

                {formData.emergencyContactName && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-2">Emergency Contact</h3>
                    <p className="text-gray-700">{formData.emergencyContactName}</p>
                    <p className="text-sm text-gray-600">{formData.emergencyContactPhone}</p>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="font-medium text-gray-900 mb-2">Amount Due</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    ${(() => {
                      const mainClassPrice = classData?.price || 0;
                      const followupTotal = followupClasses
                        .filter(c => selectedFollowupClasses.includes(c.id))
                        .reduce((sum, c) => sum + (c.price || 0) + (c.materialsFee || 0), 0);
                      return (mainClassPrice + followupTotal).toFixed(2);
                    })()}
                  </p>
                  {selectedFollowupClasses.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Main class: ${classData?.price} + Follow-up classes: ${followupClasses
                        .filter(c => selectedFollowupClasses.includes(c.id))
                        .reduce((sum, c) => sum + (c.price || 0) + (c.materialsFee || 0), 0)
                        .toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Follow-up Classes Section */}
            {followupClasses.length > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border-2 border-blue-200 p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                    <ChevronRight className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Continue Your Learning Journey</h2>
                    {classData?.followupDescription && (
                      <p className="text-sm text-gray-700 mb-4">{classData.followupDescription}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {followupClasses.map(followupClass => {
                    const isSelected = selectedFollowupClasses.includes(followupClass.id);
                    return (
                      <div
                        key={followupClass.id}
                        className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          isSelected
                            ? 'border-blue-600 bg-blue-100'
                            : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                        }`}
                        onClick={() => {
                          setSelectedFollowupClasses(prev =>
                            isSelected
                              ? prev.filter(id => id !== followupClass.id)
                              : [...prev, followupClass.id]
                          );
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <h3 className="font-semibold text-gray-900">{followupClass.title}</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  {followupClass.instructor && (
                                    <span>Instructor: {followupClass.instructor} • </span>
                                  )}
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    followupClass.skillLevel === 'Advanced' ? 'bg-red-100 text-red-700' :
                                    followupClass.skillLevel === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {followupClass.skillLevel}
                                  </span>
                                </p>

                                {/* Session dates and times */}
                                {followupClass.sessions && followupClass.sessions.length > 0 && (
                                  <div className="mt-2 text-sm text-gray-600">
                                    <div className="font-medium">
                                      {followupClass.dayOfWeek && (
                                        <span>{followupClass.dayOfWeek}s • </span>
                                      )}
                                      {followupClass.sessions[0].startTime && followupClass.sessions[0].endTime && (
                                        <span>
                                          {formatTime12Hour(followupClass.sessions[0].startTime)} - {formatTime12Hour(followupClass.sessions[0].endTime)}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs mt-1">
                                      {formatDate(followupClass.sessions[0].sessionDate)} - {formatDate(followupClass.sessions[followupClass.sessions.length - 1].sessionDate)}
                                      {followupClass.sessions.length > 1 && (
                                        <span> ({followupClass.sessions.length} sessions)</span>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {followupClass.description && (
                                  <p className="text-sm text-gray-600 mt-2">{followupClass.description}</p>
                                )}
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-lg font-bold text-gray-900">${followupClass.price}</div>
                                {followupClass.materialsFee > 0 && (
                                  <div className="text-xs text-gray-500">+${followupClass.materialsFee} materials</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {selectedFollowupClasses.length > 0 && (
                  <div className="mt-4 p-4 bg-blue-600 rounded-lg text-white">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">
                        Additional classes selected: {selectedFollowupClasses.length}
                      </span>
                      <span className="text-lg font-bold">
                        +${followupClasses
                          .filter(c => selectedFollowupClasses.includes(c.id))
                          .reduce((sum, c) => sum + (c.price || 0) + (c.materialsFee || 0), 0)
                          .toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Agreements */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Agreements</h2>
              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="termsAgreement"
                    checked={formData.termsAgreement}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    I agree to the terms and conditions *
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="photoReleaseAgreement"
                    checked={formData.photoReleaseAgreement}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    I consent to photos/videos being taken during class
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="emergencyTreatmentAgreement"
                    checked={formData.emergencyTreatmentAgreement}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    I authorize emergency medical treatment if needed
                  </span>
                </label>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleBack}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleProceedToPayment}
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? 'Processing...' : 'Proceed to Payment'}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {currentStep === 3 && clientSecret && stripePromise && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Complete Payment</h2>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripePaymentForm
                amount={(() => {
                  const mainClassPrice = classData?.price || 0;
                  const followupTotal = followupClasses
                    .filter(c => selectedFollowupClasses.includes(c.id))
                    .reduce((sum, c) => sum + (c.price || 0) + (c.materialsFee || 0), 0);
                  return mainClassPrice + followupTotal;
                })()}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PublicEnrollment;
